import { useCallback } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { RequirementsClient, type RequirementsTabId } from "@/features/requirements/RequirementsClient";
import { ProjectPicker } from "@/features/requirements/ProjectPicker";
import { listProjects, requirementsKeys } from "@/features/requirements/requirements.api";
import { applyParamUpdates } from "@/lib/searchParams";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

interface RequirementsSearch {
  project?: string;
  tab?: RequirementsTabId;
  anchor?: string;
  area?: string;
  sel?: string;
}
const TABS: RequirementsTabId[] = ["dashboard", "requirements", "function-areas", "relations", "explore"];

export const Route = createFileRoute("/_shell/requirements/")({
  // Matrix-gated module (Beheer → Rechten): admins always, other roles when
  // the "requirements" checkbox is on.
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "requirements")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): RequirementsSearch => {
    const project = typeof search.project === "string" && search.project ? search.project : undefined;
    const tab = TABS.includes(search.tab as RequirementsTabId) ? (search.tab as RequirementsTabId) : undefined;
    const anchor = typeof search.anchor === "string" && search.anchor ? search.anchor : undefined;
    const area = typeof search.area === "string" && search.area ? search.area : undefined;
    const sel = typeof search.sel === "string" && search.sel ? search.sel : undefined;
    return {
      ...(project ? { project } : {}),
      ...(tab ? { tab } : {}),
      ...(anchor ? { anchor } : {}),
      ...(area ? { area } : {}),
      ...(sel ? { sel } : {}),
    };
  },
  component: RequirementsScreen,
});

function RequirementsScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const isAdmin = session.roles.includes("admin");

  const projectsQuery = useQuery({ queryKey: requirementsKeys.projects, queryFn: listProjects });
  const projects = projectsQuery.data ?? [];
  const effectiveId = search.project ?? projects[0]?.id;

  const onParams = useCallback(
    (updates: Record<string, string>) => {
      navigate({ search: (prev) => applyParamUpdates(prev, updates), replace: true });
    },
    [navigate],
  );

  return (
    <div className="wl-page" data-screen-label="Back-office Requirements">
      {/* Which project comes before its tabs; the page itself needs no title. */}
      <div style={{ marginBottom: "16px" }}>
        <ProjectPicker projects={projects} selectedId={effectiveId} isAdmin={isAdmin} onSelect={(id) => onParams({ project: id })} />
      </div>
      <RequirementsClient isAdmin={isAdmin} userId={session.userId} projectId={effectiveId} tab={search.tab ?? "dashboard"} anchor={search.anchor} area={search.area} sel={search.sel} onParams={onParams} />
    </div>
  );
}
